import Network from 'lib/api/network';

export default {
  namespaced: true,

  state() {
    return {
      profileUser: null,
      profileEntries: [],
      profileManagers: [],
    };
  },

  mutations: {
    update(state, { key, value }) {
      state[key] = value;
    },
  },

  getters: {
    profileUser: (state) => state.profileUser,
    profileUserTeamUid: (state) => state.profileUserTeamUid,
    profileEntries: (state) => state.profileEntries,
    profileManagers: (state) => state.profileManagers,
    bannerProfileEntry: (state, getters) => {
      const myProfileEntry = getters.profileEntries.find(e => e.category.machine_name === 'my_profile');
      const bannerEntry = myProfileEntry?.entries.find(e => e.field.machine_name === 'banner_url');
      return bannerEntry;
    },
    bannerProfileEntryId: (_, getters) => getters.bannerProfileEntry?.field?.id,
    currentProfileBannerId: (state, getters) => getters.bannerProfileEntry?.text || state.defaultBannerId,
    currentProfileBannerImg: (state, getters) => state.bannersPaths[getters.currentProfileBannerId],
    areAllEntriesEmpty: (state) => {
      if (!state.profileEntries || state.profileEntries.length === 0) return true;

      const nonProfileInfoCategories = state.profileEntries.filter(categoryEntries => categoryEntries.category.machine_name !== 'my_profile');
      const result = nonProfileInfoCategories.every(category => category.entries.every(entry => !entry.text || entry.text.trim() === ''));

      return result;
    },
    currentProfileUserTime: () => (profileUser) => {
      if (!profileUser?.timezone) return '';
      const now = new Date();
      return now.toLocaleTimeString('en-US', {
        timeZone: profileUser.timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },
  },

  actions: {
    updateEntries({ commit, dispatch, state }, { userId, entries, userManagerRelationship }) {
      commit('update', { key: 'formSubmitted', value: true }, { root: true });
      const profileEntriesPromise = Network.patch(
        `/users/${userId}/user_profile_entries/bulk_upsert`,
        { user_profile_entries: entries },
        {
          success: (response) => {
            const parsedEntries = response.user_profile_entries.map(entry => JSON.parse(entry));
            const updatedProfileEntries = state.profileEntries.map(category => ({
              ...category,
              entries: category.entries.map(entry => {
                const updatedEntry = parsedEntries.find(e => e.field_id === entry.field.id);
                if (updatedEntry) {
                  return {
                    ...entry,
                    text: updatedEntry ? updatedEntry.text : '',
                  };
                }

                // meaning this entry wasn't updated
                return entry;
              }),
            }));
            commit('update', { key: 'profileEntries', value: updatedProfileEntries });
          },
          error: () => {
            dispatch('setErrorToast', 'Failed to update profile', { root: true });
          },
        },
      );
      // Skip making the user relationships call when userManagerRelationship is explicitly `undefined` (omitted from keyword args map)
      // `null` or `false` can still be passed for unsetting the user manager relationship
      let userRelationshipPromise;
      if (userManagerRelationship === undefined) {
        userRelationshipPromise = Promise.resolve();
      } else {
        userRelationshipPromise = Network.patch(
          `/users/${userId}/user_relationships/upsert`,
          {
            related_user_id: userManagerRelationship?.related_user_id || '',
            source: 'user',
          },
          {
            success: (response) => {
              commit('update', { key: 'profileManagers', value: [response] });
            },
            error: () => {
              dispatch('setErrorToast', 'Failed to update manager', { root: true });
            },
          },
        );
      }
      // Wait for all calls to complete
      return Promise.all([profileEntriesPromise, userRelationshipPromise])
        .then(() => {
          commit('closeModal', null, { root: true });
          dispatch('setSuccessToast', 'Profile updated', { root: true });
        })
        .catch(() => {
          // Error handling is done in individual network calls
        })
        .finally(() => {
          commit('update', { key: 'formSubmitted', value: false }, { root: true });
        });
    },
    updateBanner({ commit, dispatch, state, getters }, { bannerId }) {
      const { profileUser, bannerProfileEntryId } = getters;
      const userId = profileUser.id;

      const entries = [
        {
          text: bannerId,
          user_id: userId,
          field_id: bannerProfileEntryId,
        },
      ];

      dispatch('updateEntries', {
        userId,
        entries,
      });
    },
  },
};
